import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  SlidersHorizontal,
  ArrowUpDown,
  Search,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  CalendarClock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export type SortBy = "title" | "closingDate" | "createdAt";
export type SortOrder = "asc" | "desc";

interface PageHeaderProps {
  // Sort controls
  sortBy?: SortBy;
  sortOrder?: SortOrder;
  onSortByChange?: (sortBy: SortBy) => void;
  onSortOrderChange?: (sortOrder: SortOrder) => void;
  
  // Subpipeline controls
  subPipelines?: Array<{ id: string; name: string }>;
  selectedSubPipelineId?: string | null;
  onSubPipelineChange?: (id: string | null) => void;
  
  // Search
  onSearchClick?: () => void;
  
  // Filter
  onFilterClick?: () => void;

  /** CRM: show only deals whose planned close is strictly before today */
  overdueFilterActive?: boolean;
  onOverdueFilterToggle?: () => void;
  
  className?: string;
}

export default function PageHeader({
  sortBy = "createdAt",
  sortOrder = "desc",
  onSortByChange,
  onSortOrderChange,
  subPipelines = [],
  selectedSubPipelineId,
  onSubPipelineChange,
  onSearchClick,
  onFilterClick,
  overdueFilterActive = false,
  onOverdueFilterToggle,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation();
  const sortByLabels: Record<SortBy, string> = {
    title: t("crm.sortByTitle"),
    closingDate: t("crm.sortByClosingDate"),
    createdAt: t("crm.sortByCreatedAt"),
  };
  const [sortOpen, setSortOpen] = useState(false);
  const [subpipelineOpen, setSubpipelineOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSortByChange = (value: string) => {
    onSortByChange?.(value as SortBy);
    setSortOpen(false);
  };

  const handleSortOrderToggle = () => {
    onSortOrderChange?.(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleSubPipelineChange = (value: string) => {
    if (value === "all") {
      onSubPipelineChange?.(null);
    } else {
      onSubPipelineChange?.(value);
    }
    setSubpipelineOpen(false);
  };

  const selectedSubPipeline = subPipelines.find(
    (p) => p.id === selectedSubPipelineId
  );

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 px-3 h-16 bg-background border-b border-border",
        className
      )}
    >
      {/* Left: Filter and Sort */}
      <div className="flex items-center gap-2">
        {onFilterClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onFilterClick}
            className="h-9 w-9"
            aria-label={t("header.filterLabel")}
          >
            <SlidersHorizontal className="h-5 w-5" />
          </Button>
        )}

        {onOverdueFilterToggle && (
          <Button
            type="button"
            variant={overdueFilterActive ? "default" : "ghost"}
            size="icon"
            onClick={onOverdueFilterToggle}
            className="h-9 w-9"
            title={t("crm.filterOverdueOnlyHint")}
            aria-label={t("crm.filterOverdueOnly")}
            aria-pressed={overdueFilterActive}
          >
            <CalendarClock className="h-5 w-5" />
          </Button>
        )}

        {onSortByChange && (
          <Popover open={sortOpen} onOpenChange={setSortOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label={t("header.sortLabel")}
              >
                <ArrowUpDown className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start">
              <div className="space-y-2">
                <div className="px-2 py-1.5 text-sm font-semibold">
                  {t("crm.sortBy")}
                </div>
                <Select value={sortBy} onValueChange={handleSortByChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="title">{sortByLabels.title}</SelectItem>
                    <SelectItem value="closingDate">
                      {sortByLabels.closingDate}
                    </SelectItem>
                    <SelectItem value="createdAt">
                      {sortByLabels.createdAt}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSortOrderToggle}
                  className="w-full"
                >
                  {sortOrder === "asc" ? (
                    <>
                      <ArrowUp className="h-4 w-4 mr-2" />
                      Növekvő
                    </>
                  ) : (
                    <>
                      <ArrowDown className="h-4 w-4 mr-2" />
                      Csökkenő
                    </>
                  )}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}

      </div>

      {/* Center: Subpipeline Name with Dropdown */}
      {subPipelines.length > 0 && onSubPipelineChange && selectedSubPipeline && (
        <div className="flex-1 flex items-center justify-center">
          <Popover open={subpipelineOpen} onOpenChange={setSubpipelineOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-auto px-3 py-2 flex items-center justify-center gap-2 hover:bg-accent"
                aria-label={t("header.subpipelineLabel")}
              >
                <span className="font-medium text-base truncate max-w-[200px]">
                  {selectedSubPipeline.name}
                </span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="center">
              <Command>
                <CommandInput placeholder={t("common.searchPlaceholder")} />
                <CommandList>
                  <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => handleSubPipelineChange("all")}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 flex-shrink-0",
                          !selectedSubPipelineId ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {t("common.all")}
                    </CommandItem>
                    {subPipelines.map((pipeline) => (
                      <CommandItem
                        key={pipeline.id}
                        value={pipeline.name}
                        onSelect={() => handleSubPipelineChange(pipeline.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 flex-shrink-0",
                            selectedSubPipelineId === pipeline.id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <span className="truncate">{pipeline.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Right: Search */}
      <div className="flex items-center gap-2">
        {onSearchClick ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onSearchClick}
            className="h-9 w-9"
            aria-label={t("common.search")}
            data-search-trigger
          >
            <Search className="h-5 w-5" />
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="h-9 w-9"
              aria-label={t("common.search")}
              data-search-trigger
            >
              <Search className="h-5 w-5" />
            </Button>
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t("common.search")}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <Input
                    type="text"
                    placeholder={t("common.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
